import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import Button from "../../../common/inputs/Button";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import DialogTemplate from "../../../layout/DialogTemplate";
import { colors } from "../../../../styles/colors";

/** Werte, auf die alle Projekte gestellt werden. Ein leeres Feld heißt: Projektwert behalten. */
export interface VereinheitlichenWerte {
  zuschlagswert?: number;
  fremdkapitalzins?: number;
}

interface VereinheitlichenDialogProps {
  open: boolean;
  onClose: () => void;
  onStarten: (werte: VereinheitlichenWerte) => void;
}

/** Wandelt die Eingabe in eine Zahl. Leer oder unlesbar ergibt undefined. */
const alsZahl = (eingabe: string): number | undefined => {
  if (!eingabe.trim()) return undefined;
  const zahl = Number(eingabe.replace(",", "."));
  return Number.isFinite(zahl) && zahl >= 0 ? zahl : undefined;
};

/**
 * Eingabe der Werte, auf die alle Projekte zum Vergleich gestellt werden.
 *
 * Beide Felder sind optional – wer nur den Zuschlagswert vereinheitlichen will, lässt den
 * Zins leer und umgekehrt.
 */
const VereinheitlichenDialog: React.FC<VereinheitlichenDialogProps> = ({
  open,
  onClose,
  onStarten,
}) => {
  const [zuschlagswert, setZuschlagswert] = useState("");
  const [fremdkapitalzins, setFremdkapitalzins] = useState("");
  const [hinweis, setHinweis] = useState(false);

  const schliessen = () => {
    setZuschlagswert("");
    setFremdkapitalzins("");
    setHinweis(false);
    onClose();
  };

  /**
   * "Berechnen" bleibt absichtlich immer anklickbar.
   *
   * InputFieldNumber meldet den eingegebenen Wert erst beim Verlassen des Felds nach oben.
   * Ein deaktivierter Button nimmt aber keinen Klick an und löst damit auch kein Verlassen
   * aus – wer tippt und direkt klickt, käme nie weiter. So dagegen blurrt der Klick das Feld,
   * der Wert kommt an, und erst danach läuft dieser Handler.
   */
  const berechnen = () => {
    const werte: VereinheitlichenWerte = {
      zuschlagswert: alsZahl(zuschlagswert),
      fremdkapitalzins: alsZahl(fremdkapitalzins),
    };

    if (
      werte.zuschlagswert === undefined &&
      werte.fremdkapitalzins === undefined
    ) {
      setHinweis(true);
      return;
    }

    onStarten(werte);
    setZuschlagswert("");
    setFremdkapitalzins("");
    setHinweis(false);
  };

  return (
    <DialogTemplate
      open={open}
      onClose={schliessen}
      title="Werte vereinheitlichen"
      maxWidth="xs"
      actions={
        <>
          <Button variant="secondary" onClick={schliessen}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={berechnen}>
            Berechnen
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3 }}>
        Alle Projekte werden mit den eingegebenen Werten neu berechnet. Das
        Ergebnis wird nicht gespeichert.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <InputFieldNumber
          variant="floating"
          label="Zuschlagswert"
          suffix=" ct/kWh"
          value={zuschlagswert}
          onChange={setZuschlagswert}
          fullWidth
        />
        <InputFieldNumber
          variant="floating"
          label="Kreditzins"
          suffix=" % p.a."
          value={fremdkapitalzins}
          onChange={setFremdkapitalzins}
          fullWidth
        />

        {hinweis && (
          <Typography variant="body2" sx={{ color: colors.status.error.main }}>
            Bitte mindestens einen der beiden Werte eingeben.
          </Typography>
        )}
      </Box>
    </DialogTemplate>
  );
};

export default VereinheitlichenDialog;
